import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import config from '../config';
import dayjs from 'dayjs';

// EVENT LISTINGS
export function useEvents() {
  const { data: events } = useQuery(['getEvents'], async () => {
    const eventData = new FormData();
    eventData.append('auth', localStorage.getItem('token'));
    const output = await axios.post(config.EVENTS_API, eventData);
    if (output.data.response.status === 'OK') {
      return output.data.response.events;
    }
  });

  return { events };
}

// EVENT DETAILS
export function useEventDetails() {
  const { eventId } = useParams();
  const { data, isLoading } = useQuery(['getEvent', eventId], async () => {
    const getEventData = new FormData();
    getEventData.append('mode', 'getEventDetails');
    getEventData.append('eventId', eventId);
    getEventData.append('auth', localStorage.getItem('token'));

    const output = await axios.post(config.EVENTS_API, getEventData);

    if (output.data.response.status === 'OK') {
      return output.data.response;
    } else if (output.data.response.status === 'NORESULTS') {
      return 'NORESULTS';
    }
  });

  return { data, isLoading };
}

// CREATE NEW EVENT
export function useCreateNewEvent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createNewEventMutation = useMutation(async () => {
    const eventName = document.getElementById('newEventName').value;
    if (eventName !== '') {
      const data = new FormData();
      data.append('mode', 'createNewEvent');
      data.append('eventName', eventName);
      const output = await axios.post(config.EVENTS_API, data);

      if (output.data.response.status === 'OK') {
        queryClient.invalidateQueries('getEvents');
        navigate('/manage/events');
      }
    }
  });
  return { createNewEventMutation };
}

export function useAddAttendanceByName(searchString) {
  sessionStorage.removeItem('studentSearchString'); // remove any remnants of previous search strings
  const { eventId } = useParams();
  const queryClient = useQueryClient();

  // STUDENT SEARCH BY NAME OR ID
  const { data } = useQuery(
    ['studentSearch', eventId, searchString],
    async () => {
      if (searchString === '') {
        return null;
      }

      const searchData = new FormData();
      searchData.append('mode', 'studentSearchEvent');
      searchData.append('searchString', searchString);
      searchData.append('eventId', eventId);
      searchData.append(
        'searchOptions',
        document?.getElementById('searchPredicate')?.value
      );
      searchData.append('auth', localStorage.getItem('token'));

      const output = await axios.post(config.STUDENTS_API, searchData);
      if (output.data.response.status === 'OK') {
        return output.data.response.students;
      } else {
        return output.data.response.status;
      }
    }
  );

  // ADD STUDENT TO EVENT (BY NAME)
  const addUserMutation = useMutation(async studentId => {
    const userData = new FormData();
    userData.append('mode', 'addAttendance');
    userData.append('type', 'addByName');
    userData.append('studentId', studentId);
    userData.append('eventId', eventId);
    userData.append('eventDate', dayjs().format('YYYY-MM-DD'));
    userData.append('auth', localStorage.getItem('token'));
    const output = await axios.post(config.EVENTS_API, userData);

    if (output.data.response.status === 'OK') {
      queryClient.invalidateQueries(['studentSearch', eventId, searchString]);
      queryClient.invalidateQueries('getEvent', eventId);
    }
  });

  return { data, addUserMutation };
}

export function useAddAttendanceBySession() {
  const { eventId } = useParams();
  const queryClient = useQueryClient();

  const { data: sessions } = useQuery(
    ['addAttendanceBySession', 'getSessions'],
    async () => {
      const output = await axios.get(config.SESSION_API);
      return output.data.response;
    }
  );
  const addUserMutation = useMutation(async () => {
    const userData = new FormData();
    userData.append('mode', 'addAttendance');
    userData.append('type', 'addByLevel');
    userData.append('eventId', eventId);
    userData.append('level', document.getElementById('levelSelect').value);
    userData.append('session', document.getElementById('sessionSelect').value);
    userData.append(
      'eventDate',
      dayjs(document.getElementById('eventDate').value).format('YYYY-MM-DD')
    );
    userData.append('auth', localStorage.getItem('token'));
    const output = await axios.post(config.EVENTS_API, userData);
    if (output.data.response.status === 'OK') {
      queryClient.invalidateQueries('getEvent', eventId);
    }
  });

  return { sessions, addUserMutation };
}
